<template>
  <button
    class="notification-bell-button"
    :class="{ active: hasActiveRules }"
    :disabled="loading || !supported"
    :title="hasActiveRules ? 'Configurar notificaciones' : 'Activar notificaciones'"
    @click.stop.prevent="openModal"
  >
    <i
      class="pi"
      :class="loading
        ? 'pi-spin pi-spinner'
        : (hasActiveRules ? 'pi-bell' : 'pi-bell-slash')"
    ></i>
  </button>

  <teleport to="#modals">
    <div
      v-if="showModal"
      class="notification-overlay"
      @click.self="closeModal"
    >
      <div class="notification-modal">
        <h3>Notificaciones de {{ deviceName }}</h3>

        <div
          v-for="rule in draftRules"
          :key="rule.capabilityKey"
          class="notification-rule-card"
        >
          <label class="rule-header">
            <input
              type="checkbox"
              v-model="rule.enabled"
            />
            <span>{{ rule.capability.label }}</span>
          </label>

          <div
            v-if="rule.enabled"
            class="rule-body"
          >
            <!-- Evento simple, por ejemplo riego iniciado -->
            <p
              v-if="rule.capability.kind === 'event'"
              class="rule-description"
            >
              Se notificará cuando ocurra este evento.
            </p>

            <!-- Enum / boolean / binario -->
            <div
              v-if="rule.capability.kind === 'enum' || rule.capability.kind === 'boolean'"
              class="control-group"
            >
              <label>Cuándo avisar:</label>

              <select
                v-if="supportsAnyChange(rule)"
                v-model="rule.matcher.operator"
              >
                <option value="anyChange">Cualquier cambio</option>
                <option value="equals">Solo un valor concreto</option>
              </select>

              <select
                v-if="showValueSelector(rule)"
                v-model="rule.matcher.value"
              >
                <option
                  v-for="option in rule.capability.values || []"
                  :key="String(option.value)"
                  :value="option.value"
                >
                  {{ option.label }}
                </option>
              </select>
            </div>

            <!-- Sensor numérico -->
            <div
              v-if="rule.capability.kind === 'number'"
              class="control-group"
            >
              <label>Condición del valor:</label>

              <select v-model="rule.matcher.operator">
                <option value="gt">Mayor que</option>
                <option value="gte">Mayor o igual que</option>
                <option value="lt">Menor que</option>
                <option value="lte">Menor o igual que</option>
                <option value="between">Entre</option>
                <option value="outside">Fuera de rango</option>
              </select>

              <div class="number-row">
                <input
                  type="number"
                  v-model.number="rule.matcher.value"
                />

                <span v-if="rule.capability.unit">{{ rule.capability.unit }}</span>

                <template
                  v-if="['between', 'outside'].includes(rule.matcher.operator)"
                >
                  <span>y</span>

                  <input
                    type="number"
                    v-model.number="rule.matcher.value2"
                  />

                  <span v-if="rule.capability.unit">{{ rule.capability.unit }}</span>
                </template>
              </div>
            </div>

            <!-- Condición de sistema -->
            <div
              v-if="conditionOptionsFor(rule).length > 1"
              class="control-group"
            >
              <label>Condición:</label>

              <select v-model="rule.condition.mode">
                <option
                  v-for="condition in conditionOptionsFor(rule)"
                  :key="condition.value"
                  :value="condition.value"
                >
                  {{ condition.label }}
                </option>
              </select>
            </div>
          </div>
        </div>

        <div class="modal-buttons">
          <button
            class="modal-button secondary"
            @click="closeModal"
          >
            Cancelar
          </button>

          <button
            class="modal-button"
            :disabled="loading"
            @click="saveRules"
          >
            Guardar
          </button>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script>
import {
  getDeviceNotificationRules,
  saveDeviceNotificationRules
} from '@/api/notifications';

import {
  ensurePushSubscription,
  isPushSupported
} from '@/services/pushNotifications';

export default {
  name: 'NotificationBell',

  props: {
    deviceId: {
      type: String,
      required: true
    },
    deviceName: {
      type: String,
      required: true
    },
    capabilities: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      showModal: false,
      loading: false,
      supported: true,

      // Reglas realmente guardadas en backend.
      savedRules: [],

      // Copia temporal editable en el modal.
      draftRules: []
    };
  },

  computed: {
    hasActiveRules() {
      return this.savedRules.some(rule => rule.enabled);
    }
  },

  async mounted() {
    this.supported = isPushSupported();

    this.savedRules = this.buildRules([]);
    this.draftRules = this.cloneRules(this.savedRules);

    await this.loadRules();
  },

  methods: {
    cloneRules(rules) {
      return JSON.parse(JSON.stringify(rules || []));
    },

    async openModal() {
      this.loading = true;

      try {
        await this.loadRules();

        // Cada vez que se abre el modal, se edita una copia limpia
        // de lo realmente guardado.
        this.draftRules = this.cloneRules(this.savedRules);

        this.showModal = true;
      } catch (err) {
        console.error('❌ Error abriendo configuración de notificaciones:', err);
        alert('No se pudo cargar la configuración de notificaciones');
      } finally {
        this.loading = false;
      }
    },

    closeModal() {
      // Se descartan cambios no guardados.
      this.draftRules = this.cloneRules(this.savedRules);
      this.showModal = false;
    },

    async loadRules() {
      try {
        const response = await getDeviceNotificationRules(this.deviceId);

        this.savedRules = this.buildRules(response.rules || []);
        this.draftRules = this.cloneRules(this.savedRules);
      } catch (err) {
        console.error('❌ Error cargando reglas de notificación:', err);

        this.savedRules = this.buildRules([]);
        this.draftRules = this.cloneRules(this.savedRules);
      }
    },

    buildRules(existingRules) {
      const existingMap = new Map(
        existingRules.map(rule => [rule.capabilityKey, rule])
      );

      return this.capabilities.map(capability => {
        const existing = existingMap.get(capability.key);
        const defaultRule = this.defaultRuleFor(capability);

        /*
        * Importante:
        * El cooldown NO se edita en UI.
        * La definición del dispositivo en Node-RED manda sobre valores antiguos.
        */
        const cooldownSeconds = Number(
          capability.defaultCooldownSeconds ?? 0
        );

        const allowedConditionValues = this.conditionOptionsFor(capability)
          .map(condition => condition.value);

        const requestedCondition =
          existing?.condition?.mode ||
          defaultRule.condition.mode ||
          'always';

        const conditionMode = allowedConditionValues.includes(requestedCondition)
          ? requestedCondition
          : defaultRule.condition.mode;

        return {
          ...defaultRule,
          ...existing,

          /*
          * Propiedad interna para pintar la UI.
          * No se enviará al backend.
          */
          capability,

          matcher: {
            ...defaultRule.matcher,
            ...(existing?.matcher || {})
          },

          condition: {
            ...defaultRule.condition,
            ...(existing?.condition || {}),
            mode: conditionMode
          },

          cooldownSeconds: Number.isFinite(cooldownSeconds)
            ? Math.max(0, cooldownSeconds)
            : 0
        };
      });
    },

    defaultRuleFor(capability) {
      const defaultOperator =
        capability.defaultOperator ||
        (capability.kind === 'event'
          ? 'event'
          : capability.kind === 'number'
            ? 'gt'
            : capability.operators?.includes('anyChange')
              ? 'anyChange'
              : 'equals');

      const cooldownSeconds = Number(capability.defaultCooldownSeconds ?? 0);

      const allowedConditionValues = this.conditionOptionsFor(capability)
        .map(condition => condition.value);

      const conditionMode = allowedConditionValues.includes(capability.defaultCondition)
        ? capability.defaultCondition
        : allowedConditionValues[0] || 'always';

      return {
        capabilityKey: capability.key,
        eventType: capability.eventType,
        field: capability.field || null,
        enabled: false,

        /*
        * Propiedad interna de UI.
        */
        capability,

        matcher: {
          operator: defaultOperator,
          value: capability.defaultValue ?? this.defaultValueFor(capability),
          value2: capability.defaultValue2 ?? null
        },

        condition: {
          mode: conditionMode
        },

        // No se muestra en UI. Viene de Node-RED.
        cooldownSeconds: Number.isFinite(cooldownSeconds)
          ? Math.max(0, cooldownSeconds)
          : 0
      };
    },

    defaultValueFor(capability) {
      if (Array.isArray(capability.values) && capability.values.length > 0) {
        return capability.values[0].value;
      }

      if (capability.kind === 'number') {
        return 0;
      }

      return null;
    },

    supportsAnyChange(ruleOrCapability) {
      const capability = ruleOrCapability?.capability || ruleOrCapability;

      return Array.isArray(capability?.operators)
        && capability.operators.includes('anyChange');
    },

    showValueSelector(rule) {
      const capability = rule.capability;

      if (!rule) return false;
      if (!Array.isArray(capability?.values)) return false;

      return !this.supportsAnyChange(rule)
        || rule.matcher.operator === 'equals';
    },

    conditionOptionsFor(ruleOrCapability) {
      const capability = ruleOrCapability?.capability || ruleOrCapability;

      const labels = {
        always: 'Siempre',
        alarmActive: 'Solo con alarma conectada',
        alarmInactive: 'Solo con alarma desconectada'
      };

      const allowed = Array.isArray(capability?.conditions) && capability.conditions.length > 0
        ? capability.conditions
        : ['always', 'alarmActive'];

      return allowed
        .filter(value => labels[value])
        .map(value => ({
          value,
          label: labels[value]
        }));
    },

    toRulePayload(rule) {
      return {
        capabilityKey: rule.capabilityKey,
        eventType: rule.eventType,
        field: rule.field || null,
        enabled: !!rule.enabled,

        matcher: {
          operator: rule.matcher?.operator || 'event',
          value: rule.matcher?.value ?? null,
          value2: rule.matcher?.value2 ?? null
        },

        condition: {
          mode: rule.condition?.mode || 'always'
        },

        cooldownSeconds: Number.isFinite(Number(rule.cooldownSeconds))
          ? Math.max(0, Number(rule.cooldownSeconds))
          : 0
      };
    },

    async saveRules() {
      this.loading = true;

      try {
        const rulesToSave = this.draftRules.map(rule => this.toRulePayload(rule));

        if (rulesToSave.some(rule => rule.enabled)) {
          await ensurePushSubscription();
        }

        const response = await saveDeviceNotificationRules(
          this.deviceId,
          rulesToSave
        );

        this.savedRules = this.buildRules(response.rules || []);
        this.draftRules = this.cloneRules(this.savedRules);

        this.showModal = false;
      } catch (err) {
        console.error('❌ Error guardando reglas de notificación:', err);
        alert(err.message || 'No se pudo guardar la configuración de notificaciones');
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped>
.notification-bell-button {
  position: absolute;
  top: 8px;
  right: 10px;
  width: 30px;
  height: 30px;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.78);
  color: #2c3e50;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 20;
  font-size: 1rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
}

.notification-bell-button.active {
  color: #f5a400;
}

.notification-bell-button:hover:not(:disabled) {
  transform: scale(1.06);
}

.notification-bell-button:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.notification-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.notification-modal {
  width: min(92vw, 420px);
  max-height: 86vh;
  overflow-y: auto;
  background: #ffffff;
  color: #2c3e50;
  border-radius: 18px;
  padding: 18px;
  box-shadow: 0 12px 38px rgba(0, 0, 0, 0.28);
}

.notification-modal h3 {
  margin: 0 0 16px;
  text-align: center;
}

.notification-rule-card {
  border: 1px solid #e1e1e1;
  border-radius: 14px;
  padding: 12px;
  margin-bottom: 12px;
  background: #f8f8f8;
}

.rule-header {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 700;
}

.rule-body {
  margin-top: 12px;
}

.rule-description {
  margin: 0 0 10px;
  font-size: 0.9rem;
  opacity: 0.8;
}

.control-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 12px;
}

.control-group label {
  font-weight: 600;
  font-size: 0.9rem;
}

.control-group select,
.control-group input {
  border: 1px solid #ccc;
  border-radius: 10px;
  padding: 8px 10px;
  font-size: 0.95rem;
}

.number-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.number-row input {
  width: 90px;
}

.modal-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 14px;
}

.modal-button {
  border: none;
  border-radius: 12px;
  padding: 9px 16px;
  cursor: pointer;
  background: #1769e0;
  color: white;
  font-weight: 600;
}

.modal-button.secondary {
  background: #d9d9d9;
  color: #2c3e50;
}
</style>